// Expands the dictionary's notation into lookup tables, once per process.

import type { CategoryId } from "./categories.js";
import { GROUPS, type Forms, type Group } from "./dictionary.js";
import { FuzzyIndex } from "./fuzzy.js";

export const MAX_PHRASE_WORDS = 4;

export interface Entry {
  readonly alias: string;
  readonly category: CategoryId;
  readonly words: number;
  readonly attributive: boolean;
  readonly marker: boolean;
  readonly forms: Forms;
}

export interface Lexicon {
  readonly entries: ReadonlyMap<string, Entry>;
  /** Aliases listed more than once; the first listing is the one kept. */
  readonly duplicates: readonly string[];
  readonly fuzzy: () => FuzzyIndex;
}

const ENDINGS = /^([a-z0-9]*)\(([a-z/]+)\)$/;

/** "cherr(y/ies) tomato(es)" -> every spelling it stands for, in order. */
export function expandAliases(spec: string): string[] {
  const out: string[] = [];
  for (const alias of spec.split("|")) {
    let spellings = [""];
    for (const word of alias.trim().split(" ")) {
      const match = ENDINGS.exec(word);
      let forms = [word];
      if (match) {
        const stem = match[1] as string;
        const endings = (match[2] as string).split("/");
        forms = endings.length === 1 ? [stem, stem + endings[0]] : endings.map((ending) => stem + ending);
      }
      spellings = spellings.flatMap((before) => forms.map((form) => (before ? `${before} ${form}` : form)));
    }
    out.push(...spellings);
  }
  return out;
}

export function buildLexicon(groups: readonly Group[]): Lexicon {
  const entries = new Map<string, Entry>();
  const duplicates: string[] = [];
  for (const group of groups) {
    for (const alias of expandAliases(group.aliases)) {
      if (entries.has(alias)) {
        duplicates.push(alias);
        continue;
      }
      entries.set(alias, {
        alias,
        category: group.category,
        words: alias.split(" ").length,
        attributive: group.role === "attributive",
        marker: group.role === "marker",
        forms: group.forms ?? {},
      });
    }
  }
  let index: FuzzyIndex | undefined;
  const fuzzy = () => {
    index ??= new FuzzyIndex([...entries.values()].filter((entry) => entry.words <= 2));
    return index;
  };
  return { entries, duplicates, fuzzy };
}

let shared: Lexicon | undefined;

/** The lexicon for the shipped dictionary, built on first use. */
export function lexicon(): Lexicon {
  shared ??= buildLexicon(GROUPS);
  return shared;
}
